import { KeyboardEvent, ReactNode, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import classNames from 'classnames';
import { flatten } from 'lodash';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import ELEMENT, { createTestId } from 'common/element';
import { filterList } from 'common/util/array';
import { MenuItemConfig } from 'common/types';

import OverlayTrigger from 'components/common/OverlayTrigger/OverlayTrigger';
import DropdownItems from 'components/common/DropdownItems/DropdownItems';
import ClearableInput from 'components/common/ClearableInput/ClearableInput';
import { windowHeightState } from 'components/common/Managers/LayoutStateManager';

import './ComboBox.scss';
import 'components/common/Dropdown/Dropdown.scss';

type ComboBoxProps = {
  value: string;
  placeholder?: string;
  className?: string;
  items?: Array<string>;
  sections?: Array<Array<string>>;
  itemSuffixSeparator?: string;
  limit?: number;
  onChange: (value: string) => void;
  onSelect?: (value: string) => void;
  onEnter?: (value: string) => void;
  onClear?: (event: SyntheticEvent) => void;
  onFocus?: (event: SyntheticEvent) => void;
  onBlur?: (value: string) => void;
  onListChange?: (listValue: Array<string>) => void;
  required?: boolean;
  size?: 'sm' | 'lg';
  disabled?: boolean;
  testId?: ELEMENT;
  invalidError?: string;
  noResultsMsg?: string;
  selectOnEnter?: boolean;
  autoFocus?: boolean;
};

const wrapSuffix = (suffix?: string) =>
  suffix ? (
    <>
      {' '}
      <span className="ComboBox__ItemSuffix">| {suffix}</span>
    </>
  ) : (
    ''
  );

const addSuffixToItem = (item: string | ReactNode, suffix?: string) => {
  if (!suffix) {
    return item;
  }

  return (
    <>
      <span>{item}</span>
      {wrapSuffix(suffix)}
    </>
  );
};

/**
 * This component combines a text input with a dropdown list. Text in the input is used to filter the list.
 *
 * This component will render (including the dropdown) every time the input changes, so we need it to be fast.
 * One part of that is memoizing functions via the useCallback hook.
 *
 * @prop value               text content of the input
 * @prop placeholder         text to show when the input is empty
 * @prop className           additional class name(s) for the input
 * @prop items               list of items to show in the dropdown
 * @prop sections            list of sections to show in the dropdown (renders sections instead of items if sections is included as a prop)
 * @prop itemSuffixSeparator string used to split each of the items to support a suffix (different formatting)
 * @prop limit               maximum number of items to show in the dropdown
 * @prop onChange            function called with new value of text input
 * @prop onSelect            function called with user selects an item from the list
 * @prop onEnter             function to call when the user presses the Enter key
 * @prop onClear             function to call when the user has cleared the input
 * @prop onBlur              function to call when the input loses focus
 * @prop onListChange        function called with value of filtered list
 * @prop required            if true, the input is required. Useful for validation
 * @prop size                if provided, component can be smaller or larger
 * @prop disabled            whether the input is disabled
 * @prop testId              base for test IDs for input and list
 * @prop noResultsMsg        a message that renders in the dropdown if there are no results
 * @prop selectOnEnter       if true, return selected list item on Enter rather than input text
 * @prop autoFocus           if true, focus on render
 *
 * TODO: theming
 */
function ComboBox({
  value = '',
  placeholder,
  className,
  items,
  sections,
  itemSuffixSeparator,
  limit,
  onChange,
  onSelect,
  onEnter,
  onClear,
  onFocus,
  onBlur,
  onListChange,
  required = false,
  size,
  disabled = false,
  testId,
  invalidError = 'Required',
  noResultsMsg,
  selectOnEnter = false,
  autoFocus = false,
}: ComboBoxProps): JSX.Element {
  const windowHeight = useRecoilValue(windowHeightState);
  const [currentSections, setCurrentSections] = useState(sections || [items]);
  const [showOverlay, setShowOverlay] = useState(false);
  const [focusIndex, setFocusIndex] = useState(-1);

  // filter the full list and notify the parent if asked
  const filterItems = useCallback(
    (filter) => {
      if (!sections && items) {
        const curItems = filterList(items, filter, limit);
        setCurrentSections([curItems]);

        if (onListChange) {
          onListChange(curItems);
        }
      } else if (sections) {
        const curSections = sections
          .map((sectionItems) => filterList(sectionItems, filter, limit))
          .filter((sectionItems) => sectionItems.length > 0);
        setCurrentSections(curSections);
      }
    },
    [items, limit, onListChange, sections],
  );

  // filter the list with the value of the input
  useEffect(() => {
    filterItems(value);
  }, [filterItems, value]);

  // user has clicked an item in the list
  const onItemSelect = (item: string) => {
    onChange(item);
    if (onSelect) {
      onSelect(item);
    }
  };

  // Show or hide the dropdown list. If we're hiding it, reset the focus index so that no item is selected.
  const showList = (show: boolean) => {
    setShowOverlay(show);
    if (!show) {
      setFocusIndex(-1);
    }
  };

  // get the currently highlighted item in the dropdown list, if any
  const getSelectedItem = () => {
    if (!showOverlay) {
      return '';
    }
    const allItems = flatten(currentSections);
    return allItems?.[focusIndex] || '';
  };

  // handle Enter key
  const handleEnter = (text: string) => {
    if (text && onEnter && !selectOnEnter) {
      onEnter(text);
    } else {
      const item = getSelectedItem();
      if (item && onEnter) {
        onEnter(item);
      }

      if (item && !onEnter) {
        onItemSelect(item);
      }

      if (!item && onEnter) {
        onEnter(text);
      }
    }
    showList(false);
  };

  // Support autocomplete-like behavior. Tab moves the selected item to the search box (at which point running a search
  // will do "contains"). Enter is just like clicking on the selected item, if the list is showing. If not, runs a
  // "contains" search with the text in the search box.
  const handleKeyDown = (event: KeyboardEvent) => {
    let handled = true;
    const numItems = flatten(currentSections).length;
    if (event.key === 'ArrowDown') {
      setFocusIndex((index) => (index < numItems - 1 ? index + 1 : 0));
    } else if (event.key === 'ArrowUp') {
      setFocusIndex((index) => (index > 0 ? index - 1 : numItems - 1));
    } else if (event.key === 'Tab') {
      const item = getSelectedItem();
      if (item) {
        onChange(item);
      } else {
        // if list isn't showing, let browser handle Tab (move focus)
        handled = false;
      }
      showList(false);
    } else if (event.key === 'Escape') {
      // stop event propagation to avoid accidentally closing modals
      event.stopPropagation();
    } else {
      handled = false;
    }

    // eat the event if we handled it
    if (handled) {
      event.preventDefault();
    }
  };

  // call blur handler if provided
  const handleBlur = () => {
    if (onBlur) {
      onBlur(value);
    }
  };

  // handle onChange
  const handleOnChange = (item: string) => {
    if (item) {
      showList(true);
    }
    onChange(item);
  };

  // Map list of sections to item lists that have SPANS with highlighted text.
  //
  // TODO: consider adding transformation option to filterList()
  let itemIndex = -1;
  const sectionConfigs: MenuItemConfig[][] = currentSections.map((sectionItems) =>
    (sectionItems || []).map((item: string) => {
      const [itemWithoutSuffix, suffix] = (itemSuffixSeparator ? item.split(itemSuffixSeparator) : [item]).map((i) =>
        i.trim(),
      );
      itemIndex += 1;
      const isCurrentItem = itemIndex === focusIndex;
      if (!value) {
        return {
          children: addSuffixToItem(itemWithoutSuffix, suffix),
          text: item,
          clickHandler: () => onItemSelect(item),
          focused: isCurrentItem,
        };
      }
      let children = addSuffixToItem(itemWithoutSuffix, suffix);
      if (!value.includes('*')) {
        const idx = itemWithoutSuffix.toLowerCase().indexOf(value.toLowerCase());
        const preText = idx >= 0 ? itemWithoutSuffix.substring(0, idx) : itemWithoutSuffix;
        const innerText = idx >= 0 ? itemWithoutSuffix.substring(idx, idx + value.length) : '';
        const postText = idx >= 0 ? itemWithoutSuffix.substring(idx + value.length) : '';
        children = (
          <span>
            {preText}
            <span className="bold">{innerText}</span>
            {postText}
            {wrapSuffix(suffix)}
          </span>
        );
      }

      return {
        children,
        clickHandler: () => onItemSelect(item),
        focused: isCurrentItem,
      };
    }),
  );

  // renders a message section when there are no results after filtering
  if (sectionConfigs.length === 0 && noResultsMsg) {
    sectionConfigs.push([
      {
        text: noResultsMsg,
        disabled: true,
      },
    ]);
  }

  // A custom popper.js modifier to modify dropdown menu styles.
  // This is a good enough solution to set maxHeight for our use case.
  // For a more sophisticated solution, consider using this modifier
  // https://github.com/atomiks/popper.js/tree/master/packages/popper-max-size-modifier
  const popperConfig = {
    modifiers: [
      {
        name: 'applyComboBoxStyles',
        enabled: true,
        phase: 'write',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        fn: ({ state }: any) => {
          const offsetY = state.modifiersData.popperOffsets?.y || 0;
          // offsetY is the vertical starting position of the menu. 30 gives the menu some bottom buffer.
          state.styles.popper.maxHeight = windowHeight - offsetY - 30 + 'px';
        },
      },
    ],
  };

  const dropdownBody = <DropdownItems sections={sectionConfigs} close={() => showList(false)} testId={testId} />;

  const fontSize = size && (size === 'lg' ? 'large' : 'small');

  return (
    <div>
      <OverlayTrigger
        placement="bottom-start"
        overlay={dropdownBody}
        popoverClass="ComboBox__Dropdown Dropdown__Menu"
        showOverlay={showOverlay}
        setShowOverlay={showList}
        popperConfig={popperConfig}
        disabled={disabled}
      >
        <ClearableInput
          value={value}
          placeholder={placeholder}
          className={classNames('ComboBox__Input', className)}
          onChange={handleOnChange}
          onEnter={handleEnter}
          onKeyDown={handleKeyDown}
          onClear={onClear}
          onBlur={handleBlur}
          required={required}
          size={size}
          rightIcon={<ArrowDropDownIcon fontSize={fontSize} />}
          disabled={disabled}
          testId={testId && createTestId(testId, 'input')}
          invalidError={invalidError}
          onFocus={onFocus}
          autoFocus={autoFocus}
        />
      </OverlayTrigger>
    </div>
  );
}

export default ComboBox;
